// Constants
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { SupportedReferenceTypes } from "@ms/uno-constants/lib/local/FileTypeConstants";
import { NotificationId } from "@ms/uno-constants/lib/local/NotificationConstants";
import { MinCharsNeededToSearch } from "@ms/uno-constants/lib/local/UIBehaviorConstants";
// Controls
import { ProgressIndicator as AnimatedProgressIndicator, DirectionalHint, FocusZone, FocusZoneDirection, getPersonaInitialsColor, Image, ImageFit, ImageLoadState, OverflowButtonType, Persona, PersonaSize } from "@fluentui/react";
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
import { ContextualMenu } from "../../../contextualMenu/ContextualMenu";
import { AgentStatusIndicator } from "../../agentStatusIndicator/AgentStatusIndicator";
import { AddIconPlacement, LayoutStyle, TaskAssignmentControl } from "../../assignmentControl/TaskAssignmentControl";
import { Checkbox } from "../../checkbox/Checkbox";
import { DocumentPreview } from "../../documentPreview/DocumentPreview";
import { DocumentPreviewIcon } from "../../documentPreviewIcon/DocumentPreviewIcon";
import { LabelTag } from "../../labelTag/LabelTag";
import { CompleteTaskButtonWrapper } from "../../legacyPlanner/completeTaskAnimationButton/CompleteTaskButtonWrapper";
import { AppPoweredTaskIndicator } from "../../taskIndicators/AppPoweredTaskIndicator";
import { AttachmentIndicator } from "../../taskIndicators/AttachmentIndicator";
import { ChecklistIndicator } from "../../taskIndicators/ChecklistIndicator";
import { ConversationIndicator } from "../../taskIndicators/ConversationIndicator";
import { DueDateIndicator as DueDateIndicatorConvergence } from "../../taskIndicators/DueDateIndicator";
import { MyDayIndicator } from "../../taskIndicators/MyDayIndicator";
import { PriorityIndicator as PriorityIndicatorConvergence } from "../../taskIndicators/PriorityIndicator";
import { ProgressIndicator as ProgressIndicatorConvergence } from "../../taskIndicators/ProgressIndicator";
import { UserContainer } from "../../user/UserContainer";
import { TaskContextMenu } from "./TaskContextMenu";
import { PreviewType } from "@ms/uno-models/lib/local/client/legacyPlanner/previewType/PreviewType";
import { ProgressState } from "@ms/uno-models/lib/local/client/legacyPlanner/progressState/ProgressState";
import { ChecklistItem } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ChecklistItem";
import { PriorityName } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
import { ChecklistPermissions, isChecklistPermissionDisabled } from "@ms/uno-models/lib/local/client/permissions/ChecklistPermissions";
import { TaskAttachmentRowItem } from "@ms/uno-models/lib/local/client/taskAttachment/TaskAttachmentRowItem";
import { UnknownUser } from "@ms/uno-models/lib/local/client/user/UnknownUser";
import { ViewContext } from "@ms/uno-models/lib/local/contexts/IViewContext";
// Strings
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { classNamesFunction, styled, useTheme } from "@fluentui/react";
import { documentPreviewIconsOverrides, getDocumentPreviewIconStyles } from "../../documentPreview/DocumentPreview.styles";
import { customAgentStatusIndicatorStyles, getStyles, TaskCardTaskAssignmentControlStyles } from "./TaskCard.styles";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { css } from "@fluentui/utilities/lib/css";
import { getInitials } from "@fluentui/utilities/lib/initials";
import { getRTL } from "@fluentui/utilities/lib/rtl";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { ConvertPercentCompleteToProgressState, ConvertPriorityToPriorityName } from "@ms/uno-models/lib/local/client/legacyPlanner/utilities/ConversionUtilities";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { compareNumbers, compareOrderHints } from "@ms/uno-utilities/lib/local/Comparators";
import { FormatDate } from "@ms/uno-utilities/lib/local/DateUtilities";
import { ChangeFocusBeforeRemovalIFocusable } from "@ms/uno-utilities/lib/local/FocusUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import forEach from "lodash/forEach";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import moment from "moment";
import React, { useCallback, useContext, useRef } from "react";
import { useLoggers } from "../../../../hooks/useLoggers";
import { useVisibleState } from "../../../../hooks/useVisibleState";
import { extractContentFromHtmlForNotesPreview } from "../../../../utilities/HtmlNotesPreviewUtilities";
import { getLabelPlaceholder } from "../../../../utilities/LabelUtilities";
/** Callback to generate classnames for the component styles */ const getClassNames = classNamesFunction();
/** Strings for assignment control */ const assignmentControlStrings = {
    // Leaving assignControlLauncherText as empty string since we don't want to show title with add assignment icon
    assignControlLauncherText: "",
    assignControlIconTitle: Strings.TaskAssignments_AssignControlLauncherText,
    assignControlAssignToMeButtonText: Strings.TaskAssignments_AssignToMeLabel,
    readonlyFieldPermissionsTooltip: Strings.SharedComponents_ReadonlyFieldPermissionsTooltip,
    assignControlAriaLabelSuffix: Strings.TaskAssignments_AssignControlAriaLabelSuffix,
    assignControlAriaLabelPrefix: Strings.TaskAssignments_AssignControlAriaLabelPrefix,
    assignControlAriaLabelPrefixNoAssignees: Strings.TaskAssignments_AssignControlAriaLabelPrefixNoAssignees,
    assignControlListSeparator: Strings.TaskAssignments_AssignControlAriaLabelListSeparator,
    assignControlAriaLabelConstruction: Strings.TaskAssignments_AssignControlAriaLabelConstruction,
    assignmentFilterPlaceholderText: Strings.TaskAssignments_AssignFilterPlaceholder,
    assignmentFilterAriaLabel: Strings.TaskAssignments_AssignFilterPlaceholder,
    noResultsFoundText: Strings.TaskAssignments_NoResultsFoundText,
    giveAccessTooltipText: Strings.TaskAssignments_GiveAccessButtonAriaLabel,
    unassignUserButtonText: Strings.TaskAssignments_UnassignUserButtonAriaLabel,
    assignedSectionHeader: Strings.TaskAssignments_AssignedSectionHeader,
    queryLoadingText: Strings.TaskAssignments_LoadingText,
    suggestedSectionHeader: Strings.TaskAssignments_SuggestedSectionHeader,
    personaRoleGuest: Strings.TaskAssignments_PersonaRoleGuest,
    personaRoleNonMember: Strings.TaskAssignments_PersonaRoleNonMember,
    personaCompoundRole: Strings.TaskAssignments_PersonaCompoundSubText,
    suggestedAssigneeMostAvailableUser: Strings.TaskAssignments_SuggestedAssigneeMostAvailableUser,
    suggestedAssigneePlanTopAssignee: Strings.TaskAssignments_SuggestedAssigneePlanTopAssignee,
    suggestedAssigneeSimilarTitle: Strings.TaskAssignments_SuggestedAssigneeSimilarTitle,
    suggestedAssigneeUserTopAssignee: Strings.TaskAssignments_SuggestedAssigneeUserTopAssignee
};
/** Strings for PriorityIndicator */ const priorityIndicatorStrings = {
    priorityUrgentText: Strings.Task_PriorityUrgentText,
    priorityImportantText: Strings.Task_PriorityImportantText,
    priorityMediumText: Strings.Task_PriorityMediumText,
    priorityLowText: Strings.Task_PriorityLowText
};
/** Strings for ProgressIndicator */ const progressIndicatorStrings = {
    completedText: Strings.Task_CompletedText,
    inProgressText: Strings.Task_InProgressText,
    notStartedText: Strings.Task_NotStartedText
};
/** Base component to be wrapped with `styled` */ function TaskCardBase({ task, taskBucket, taskDetails, taskPlan, taskPlanLabels, callbacks, showBucketTitle, showPlanTitle, showPreview, showCopyLinkToTask, highlightType, maxAssignees, completeBtnRef, moreBtnRef, isFocusZoneEnabled, currentUserId, taskPermissions, canAddGuests, maxDisplayablePersonas, availableAgents, onDelete, onFocusContainer, onTaskCardClick, disableFocusZone, styles, isAITask, agentsInfoContentMap }) {
    const { traceLogger, engagementLogger } = useLoggers();
    const theme = useTheme();
    const viewContext = useContext(ViewContext);
    const bottomBarRef = useRef(null);
    const containerRef = useRef(null);
    const bucketMenuButtonRef = useRef(null);
    /** Checklist item elements for managing focus */ const checklistItemFocusableElements = useRef([]);
    //#region State
    /** People picker visible state and callbacks */ const { isItemVisible: isPeoplePickerVisible, showItem: showPeoplePicker, hideItem: hidePeoplePicker } = useVisibleState();
    /** Bucket menu visible state and callbacks */ const { isItemVisible: isBucketMenuVisible, toggleOnEvent: toggleBucketMenu, hideItem: hideBucketMenu } = useVisibleState();
    /** Task preview image load state */ const [taskPreviewImageLoadState, setTaskPreviewImageLoadState] = React.useState(ImageLoadState.notLoaded);
    /** Value indicates if labels are hovered */ const [areLabelsHovered, setAreLabelsHovered] = React.useState(false);
    //#endregion
    //#region Utilities
    /** Get assignees of the task */ const getAssignees = ()=>map(task.getAssigneeIds(), (userId)=>{
            let user = callbacks.getUser(userId);
            if (!user) {
                // The user id was not found in the store
                user = UnknownUser.getUnknownUser(userId);
            }
            return user;
        });
    /** Check if the previewtype a reference or automatic preview type */ const isPreviewTypeReferenceOrAutomatic = ()=>[
            PreviewType.Reference,
            PreviewType.Automatic
        ].includes(task.previewType);
    /**
     * Used to stop event propagations so that task details dialog won't open
     * @param evt The event that triggered this method
     */ const stopPropagation = (evt)=>{
        evt.stopPropagation();
    };
    /** Log engagement with component name */ const logEngagement = useCallback((action, extraData, exportName)=>{
        engagementLogger.logEngagement(EngagementTelemetryConstants.TaskCard, action, extraData, exportName);
    }, [
        engagementLogger
    ]);
    //#endregion
    //#region Callbacks
    /** Callback for when the mouse enters a label */ const handleLabelMouseEnter = useCallback(()=>setAreLabelsHovered(true), []);
    /** Callback for when the mouse leaves a label */ const handleLabelMouseLeave = useCallback(()=>setAreLabelsHovered(false), []);
    /**
     * Toggles the arrow key navigation within a FocusZone
     * @param event Simple user interface events
     */ const disableFocusZoneCallback = useCallback((event)=>{
        const key = KeyboardUtilities.getKey(event);
        if (key === KeyCode.TAB) {
            // Disable focus zone when user exits a task card using tab key
            disableFocusZone();
        }
    }, [
        disableFocusZone
    ]);
    /**
     * When a tile is clicked or the user pushes enter while the tile is focused, we want to trigger the onclick event passed.
     * @param evt: The event that caused this method to be called.  Can be a click or a button press.
     *             If it's a button press, we only want to continue with execution if it's the ENTER button, otherwise, ignore
     */ const onCardClick = useCallback((evt)=>{
        if (KeyboardUtilities.isButtonPress(evt)) {
            if (onTaskCardClick != null) {
                onTaskCardClick(task.id);
                evt.stopPropagation();
            }
        }
    }, [
        task,
        onTaskCardClick
    ]);
    /** Called when the complete button is clicked */ const onTaskCardCompleteClick = useCallback(()=>{
        const updatedTask = task.toggleCompletionState();
        callbacks.onUpdateTask(task, updatedTask);
        const engagementAction = task.isComplete() ? EngagementTelemetryConstants.TaskReactivateButtonClicked : EngagementTelemetryConstants.TaskCompleteButtonClicked;
        logEngagement(engagementAction, {
            taskCreationSourceType: task.taskCreationSourceType ?? ""
        });
    }, [
        task,
        callbacks,
        logEngagement
    ]);
    /** Callback for getting users in this task's assignee pool */ const getAssigneePoolCallback = useCallback(()=>{
        return callbacks.getAssigneePool(task.id) ?? [];
    }, [
        task.id,
        callbacks
    ]);
    /** Helper to get the list of users for a zero-term query */ const getZeroTermQueryAssignmentResults = useCallback(()=>{
        return callbacks.getAssigneeSuggestions(task.planId, task);
    }, [
        task,
        callbacks
    ]);
    /**
     * Callback for when an agent is assigned to a task
     */ const onAssignAgent = useCallback((agentId)=>{
        if (task === null) {
            return;
        }
        callbacks.onAssignAgent(agentId, task.id);
    }, [
        task,
        callbacks
    ]);
    /**
     * Callback for when an agent is unassigned from a task
     */ const onUnassignAgent = useCallback((agentId)=>{
        if (task === null) {
            return;
        }
        callbacks.onUnassignAgent(agentId, task.id);
        if (isPeoplePickerVisible) {
            hidePeoplePicker();
        }
    }, [
        task,
        callbacks,
        isPeoplePickerVisible,
        hidePeoplePicker
    ]);
    /**
     * Callback for when a user is added via the people picker
     * @param userId User to add
     * @param elementToFocusOnDismiss HTML element to focus on when dismissing warning dialog
     * @param onAssignSuccess Callback for when the task assignment is successful
     */ const onAssignUser = useCallback((userId, elementToFocusOnDismiss, onAssignSuccess)=>{
        if (task == null || callbacks.onAssignTask == null) {
            return;
        }
        if (task.getAssigneeIds().indexOf(userId) === -1) {
            callbacks.onAssignTask(userId, task.id, elementToFocusOnDismiss, onAssignSuccess);
        }
    }, [
        task,
        callbacks
    ]);
    /** Callback for when a user is unassigned from this task via the people picker */ const onUnassignUser = useCallback((userId)=>{
        if (task == null || callbacks.onUpdateTask == null) {
            return;
        }
        if (task.getAssigneeIds().indexOf(userId) !== -1) {
            const updatedTask = task.getClone();
            delete updatedTask.assignments[userId];
            if (isEmpty(updatedTask.assignments) && isPeoplePickerVisible) {
                hidePeoplePicker();
            }
            callbacks.onUpdateTask(task, updatedTask);
        }
    }, [
        task,
        callbacks,
        isPeoplePickerVisible,
        hidePeoplePicker
    ]);
    /**
     * Helper to give user access to a plan
     * @param userId The id of the user to add
     */ const giveUserAccessToPlan = useCallback((userId)=>{
        callbacks.addUserToContainer(userId, task.planId);
    }, [
        task,
        callbacks
    ]);
    /** When assign callout is opened, update task card state and call corresponding callback */ const onOpenAssignCallout = useCallback(()=>{
        showPeoplePicker();
        if (callbacks.onOpenAssignCallout) {
            callbacks.onOpenAssignCallout(task.planId);
        }
        if (task.creationSource?.publication != null) {
            callbacks.fetchTaskContainerPermissions(task);
        }
    }, [
        task,
        callbacks,
        showPeoplePicker
    ]);
    /** Show the assignee limit error dialog */ const showAssigneeLimitError = useCallback(()=>{
        callbacks.showAssigneeLimitError(task.id);
    }, [
        task,
        callbacks
    ]);
    /**
     * Helper to trigger a hidden toast alert regarding the tenant user search results
     * @param numResults The number of results
     */ const tenantSearchAlertUser = useCallback((numResults)=>{
        callbacks.alertUser("Toasts_TenantUserSearchResultsToastTitle", [
            numResults.toString()
        ]);
    }, [
        callbacks
    ]);
    /**
     * When preview checkbox was clicked, fire off an update
     * @param checklistItem The checklist item that was changed
     * @param event The event that triggered a change
     */ const onPreviewCheckboxChanged = useCallback((checklistItem, event)=>{
        event?.stopPropagation();
        event?.preventDefault();
        ChangeFocusBeforeRemovalIFocusable(checklistItemFocusableElements.current, onFocusContainer, checklistItem.id);
        if (taskDetails != null) {
            const updatedTaskDetails = taskDetails.checkChecklistItem(checklistItem.id, true);
            callbacks.onUpdateTaskDetails(taskDetails, updatedTaskDetails);
            logEngagement(EngagementTelemetryConstants.ChecklistItemCompleted);
        }
    }, [
        callbacks,
        taskDetails,
        onFocusContainer,
        logEngagement
    ]);
    const onClickIndicator = useCallback(()=>{
        if (task.creationSource?.publication != null) {
            callbacks.fetchTaskContainerPermissions(task);
        }
    }, [
        task,
        callbacks
    ]);
    /** Set the bucket for the task */ const onClickBucketMenuItem = useCallback((ev, item)=>{
        const updatedTask = task.setProperty("bucketId", item?.key ?? null);
        callbacks.onUpdateTask(task, updatedTask);
        hideBucketMenu();
    }, [
        task,
        callbacks,
        hideBucketMenu
    ]);
    /**
     * When an attachment is clicked, we want to open a new tab using this attachments url and log that the user has done this.
     * @param attachmentUrl Attachment url to go to when clicked. If null, we don't go or log.
     * @param event The event that triggered this call
     */ const onAttachmentClick = useCallback((attachmentUrl, event)=>{
        event.stopPropagation();
        event.preventDefault();
        if (attachmentUrl) {
            logEngagement(EngagementTelemetryConstants.AttachmentClicked);
            try {
                // This should never be undefined so ...
                callbacks.onOpenLink(taskDetails.references[attachmentUrl]);
            } catch (error) {
                callbacks.showInfoBanner(NotificationId.UnopenableAttachmentLinkInfo);
            }
        }
        if (task.creationSource?.publication != null) {
            callbacks.fetchTaskContainerPermissions(task);
        }
    }, [
        task,
        taskDetails,
        callbacks,
        logEngagement
    ]);
    /** Callback to open the copy task dialog */ const openCopyTaskDialogCallback = useCallback((taskId)=>{
        callbacks.openCopyTaskDialog(taskId, onFocusContainer);
    }, [
        callbacks,
        onFocusContainer
    ]);
    /** Callback to open the move task dialog */ const openMoveTaskDialogCallback = useCallback((taskId)=>{
        callbacks.openMoveTaskDialog(taskId, onFocusContainer);
    }, [
        callbacks,
        onFocusContainer
    ]);
    /** Callback to open the delete recurring task series dialog */ const openDeleteRecurringTaskDialogCallback = useCallback((taskId)=>{
        callbacks.openDeleteRecurringTaskDialog(taskId, onFocusContainer);
    }, [
        callbacks,
        onFocusContainer
    ]);
    //#endregion
    //#region Render
    const taskProgressState = ConvertPercentCompleteToProgressState(task.percentComplete);
    const isChecklistDisabled = !taskPermissions.canEdit || isChecklistPermissionDisabled(taskPermissions.checklistPermissions, ChecklistPermissions.DisableCheckedStateToggling);
    const classNames = getClassNames(styles, {
        theme,
        isComplete: task.isComplete(),
        assigneeHighlighted: highlightType.assigneeHighlighted,
        isChecklistDisabled
    });
    /**
     * Render the fallback thumbnail for an attachment
     * @param attachment Attachment to render fallback thumbnail for
     */ const renderFallbackThumbnail = (attachment)=>{
        const taskAttachmentRowItem = TaskAttachmentRowItem.builder.fromTaskAttachment(attachment, "").build();
        const fileType = taskAttachmentRowItem.taskAttachmentRowItemType.taskAttachmentType;
        const customStyle = getDocumentPreviewIconStyles(theme, fileType);
        return /*#__PURE__*/ React.createElement("div", {
            className: css(classNames.thumbnail, "placeholder")
        }, /*#__PURE__*/ React.createElement(DocumentPreviewIcon, {
            key: taskAttachmentRowItem.url,
            styles: customStyle,
            taskAttachmentRowItem: taskAttachmentRowItem,
            className: "fallbackPreviewIcon",
            icons: documentPreviewIconsOverrides
        }));
    };
    /**
     * Render a known reference preview if one of the below is true:
     * - Preview type is Reference or Automatic
     * - Number of References is non-zero
     * - Not an "other" type or is a special case we know how to render
     */ const renderReferencePreviewThumbnail = ()=>{
        if (!showPreview) {
            return null;
        }
        // Check for correct preview type and verify number of references as automatic previewtype can exist regardless of references
        if (!isPreviewTypeReferenceOrAutomatic() || task.numberOfReferences <= 0) {
            return null;
        }
        // If task details has not been loaded yet, render the placeholder
        if (taskDetails == null) {
            return /*#__PURE__*/ React.createElement("div", {
                className: css(classNames.thumbnail, "placeholder")
            });
        }
        const referenceWithLowestPriority = taskDetails.getReferenceWithLowestPriority();
        // We need a reference with a type that isn't other OR is a special type we can handle
        if (!referenceWithLowestPriority?.taskAttachmentType || referenceWithLowestPriority?.taskAttachmentType?.taskAttachmentType === SupportedReferenceTypes.TeamsHostedApp) {
            return null;
        }
        if (!taskPlan) {
            return renderFallbackThumbnail(referenceWithLowestPriority);
        }
        const thumbnail = callbacks.getDocumentPreviewThumbnail(taskPlan.container.externalId, referenceWithLowestPriority.url);
        if (thumbnail == null || taskPreviewImageLoadState === ImageLoadState.error) {
            return renderFallbackThumbnail(referenceWithLowestPriority);
        } else {
            return /*#__PURE__*/ React.createElement("div", {
                className: css(classNames.thumbnail, "real")
            }, /*#__PURE__*/ React.createElement(Image, {
                className: "previewImage",
                shouldFadeIn: true,
                imageFit: ImageFit.none,
                src: thumbnail,
                alt: "",
                onLoadingStateChange: setTaskPreviewImageLoadState
            }));
        }
    };
    /** Render the task context menu */ const renderTaskMenu = ()=>{
        const taskBoardConfig = {
            enableMoveTaskOperation: taskPermissions.canEdit && taskPermissions.canMove
        };
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.taskMenuSection
        }, /*#__PURE__*/ React.createElement(TaskContextMenu, {
            currentUserId: currentUserId,
            config: taskBoardConfig,
            task: task,
            labels: taskPlanLabels,
            showAssign: taskPermissions.canEdit && taskPermissions.canAssign,
            showDelete: taskPermissions.canEdit && taskPermissions.canDelete,
            showLabel: taskPermissions.canEdit && taskPermissions.canUpdateAppliedLabels,
            showCopyTask: taskPermissions.canEdit && taskPermissions.canCopy,
            showCopyLinkToTask: showCopyLinkToTask,
            openCopyTaskDialog: openCopyTaskDialogCallback,
            openMoveTaskDialog: openMoveTaskDialogCallback,
            openDeleteRecurringTaskDialog: openDeleteRecurringTaskDialogCallback,
            onCopyLinkToTask: callbacks.onCopyLinkToTask,
            deleteTask: onDelete,
            onFocusContainer: onFocusContainer,
            onUpdateTask: callbacks.onUpdateTask,
            onClickAssign: showPeoplePicker,
            onOtherCommandsMenuOpened: hidePeoplePicker,
            toggleMyDayForTask: callbacks.toggleMyDayForTask,
            isFocusZoneEnabled: isFocusZoneEnabled,
            disableFocusZone: disableFocusZoneCallback,
            otherCommandsMenuCommandButton: moreBtnRef,
            fetchTaskContainerPermissions: callbacks.fetchTaskContainerPermissions,
            styles: classNames.subComponentStyles.taskContextMenu
        }));
    };
    /**
     * Render a category label for the task.
     * @param labelIndex Index of the label to render.
     * @param isCollapsed Whether the labels are currently collapsed for this plan
     */ const renderLabel = (labelIndex, isCollapsed)=>{
        const label = taskPlanLabels[labelIndex];
        if (label == null) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("li", {
            key: labelIndex
        }, /*#__PURE__*/ React.createElement(LabelTag, {
            key: labelIndex,
            index: labelIndex,
            isHovered: areLabelsHovered,
            title: getLabelPlaceholder(label),
            isCollapsed: isCollapsed,
            showBorder: areLabelsHovered && !isCollapsed,
            onClick: (evt)=>{
                evt.stopPropagation();
                callbacks.setLabelsCollapsedState(!isCollapsed, task.planId);
            },
            onMouseEnter: handleLabelMouseEnter,
            onMouseLeave: handleLabelMouseLeave
        }));
    };
    /** Render the agent labels in the card */ const renderCopilotWorkflowStateLabel = ()=>{
        const copilotWorkflowStateLabel = callbacks.getCopilotWorkflowStatusLabel(task.id);
        if (!copilotWorkflowStateLabel) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("li", {
            key: copilotWorkflowStateLabel.id
        }, /*#__PURE__*/ React.createElement(LabelTag, {
            key: copilotWorkflowStateLabel.id,
            index: copilotWorkflowStateLabel.index,
            isHovered: areLabelsHovered,
            title: copilotWorkflowStateLabel.name ?? "",
            isCollapsed: viewContext.isLabelCollapsed,
            iconProps: copilotWorkflowStateLabel.iconProps,
            showBorder: areLabelsHovered && !viewContext.isLabelCollapsed,
            onClick: (evt)=>{
                evt.stopPropagation();
                callbacks.setLabelsCollapsedState(!viewContext.isLabelCollapsed, task.planId);
            },
            onMouseEnter: handleLabelMouseEnter,
            onMouseLeave: handleLabelMouseLeave
        }));
    };
    /** Render the category labels in the card */ const renderCategoryLabels = ()=>{
        const appliedCategories = task.appliedCategories.sort(compareNumbers);
        if (isEmpty(appliedCategories) || taskPlanLabels.length === 0) {
            return null;
        }
        const isLabelCollapsed = viewContext.isLabelCollapsed;
        return /*#__PURE__*/ React.createElement(React.Fragment, null, appliedCategories.map((categoryIndex)=>{
            return renderLabel(categoryIndex, isLabelCollapsed);
        }));
    };
    /** Render the labels in the card */ const renderLabels = ()=>{
        const appliedCategories = task.appliedCategories.sort(compareNumbers);
        const copilotWorkflowStateLabel = callbacks.getCopilotWorkflowStatusLabel(task.id);
        const labelsAriaLabels = [];
        // Generate aria-label for the copilot workflow state label
        if (copilotWorkflowStateLabel?.name) {
            labelsAriaLabels.push(copilotWorkflowStateLabel.name);
        }
        // Generate aria-label for the category labels
        forEach(appliedCategories, (appliedCategory)=>{
            const label = taskPlanLabels[appliedCategory];
            if (label != null) {
                labelsAriaLabels.push(getLabelPlaceholder(label));
            }
        });
        const labelsAriaLabel = labelsAriaLabels.join(", ");
        const isLabelCollapsed = viewContext.isLabelCollapsed;
        return /*#__PURE__*/ React.createElement("ul", {
            className: "labels",
            "aria-label": labelsAriaLabel,
            "data-is-focusable": isFocusZoneEnabled,
            tabIndex: isFocusZoneEnabled ? 0 : -1,
            onClick: (evt)=>{
                // Do not open task details from this element + keyboard
                // Task details will open if user clicks the blank label space with a mouse
                // If the onClick handler is invoked by keyboard, these values will be 0 or undefined
                if (!evt.screenX && !evt.screenY) {
                    evt.stopPropagation();
                }
            },
            onKeyUp: (evt)=>{
                if (KeyboardUtilities.isButtonPress(evt)) {
                    evt.stopPropagation();
                    callbacks.setLabelsCollapsedState(!isLabelCollapsed, task.planId);
                }
            },
            onKeyDown: disableFocusZoneCallback
        }, renderCopilotWorkflowStateLabel(), renderCategoryLabels());
    };
    /** Render the complete button */ const renderCompleteButton = ()=>{
        return /*#__PURE__*/ React.createElement(CompleteTaskButtonWrapper, {
            task: task,
            taskDetails: taskDetails,
            getFlights: callbacks.getFlights,
            isFocusZoneEnabled: isFocusZoneEnabled,
            completeButtonRef: completeBtnRef,
            isDisabled: !taskPermissions.canUpdateProgress,
            onButtonClick: onTaskCardCompleteClick,
            disableTaskCardFocusZone: disableFocusZoneCallback,
            theme: theme,
            isAITask: isAITask
        });
    };
    /** Render task priority indicator */ const renderPriorityIndicator = ()=>{
        const taskPriorityName = ConvertPriorityToPriorityName(task.priority);
        if (taskPriorityName === PriorityName.Low || taskPriorityName === PriorityName.Medium) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(PriorityIndicatorConvergence, {
            isKeyboardFocusable: isFocusZoneEnabled,
            priority: task.priority,
            priorityStrings: priorityIndicatorStrings,
            taskPermissions: taskPermissions,
            onKeyDown: disableFocusZoneCallback,
            onMenuClick: onClickIndicator,
            onUpdatePriority: (priority)=>{
                const updatedTask = task.setProperty("priority", priority);
                callbacks.onUpdateTask(task, updatedTask);
            }
        });
    };
    /** Render task progress indicator */ const renderInProgressIndicator = ()=>{
        // Only show indicator for in progress task
        if (taskProgressState !== ProgressState.InProgress) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(ProgressIndicatorConvergence, {
            ariaLabel: Strings.Task_InProgressText,
            canUpdateProgress: taskPermissions.canUpdateProgress,
            isKeyboardFocusable: isFocusZoneEnabled,
            percentComplete: task.percentComplete,
            progressStrings: progressIndicatorStrings,
            onKeyDown: disableFocusZoneCallback,
            onMenuClick: onClickIndicator,
            onUpdateProgress: (percentComplete)=>{
                const updatedTask = task.setProperty("percentComplete", percentComplete);
                callbacks.onUpdateTask(task, updatedTask);
            }
        });
    };
    /** Render the indicator row in the task card */ const renderIndicatorRow = ()=>{
        const conversationIndicator = task.conversationThreadId != null || task.hasChat ? /*#__PURE__*/ React.createElement(ConversationIndicator, {
            ariaLabel: Strings.TaskCard_HasCommentsAriaLabel,
            isKeyboardFocusable: isFocusZoneEnabled,
            onKeyDown: disableFocusZoneCallback
        }) : null;
        const numberOfReferencesToShow = task.getNumberOfVisibleReferences();
        const attachmentIndicator = numberOfReferencesToShow > 0 ? /*#__PURE__*/ React.createElement(AttachmentIndicator, {
            isKeyboardFocusable: isFocusZoneEnabled,
            // We do not show reference for apt task
            numberOfAttachments: numberOfReferencesToShow,
            onKeyDown: disableFocusZoneCallback
        }) : null;
        const appPoweredTaskIndicator = task.isAppPoweredTask() ? /*#__PURE__*/ React.createElement(AppPoweredTaskIndicator, {
            isKeyboardFocusable: isFocusZoneEnabled,
            onKeyDown: disableFocusZoneCallback
        }) : null;
        const checkListIndicator = task.numberOfChecklistItems > 0 ? /*#__PURE__*/ React.createElement(ChecklistIndicator, {
            isKeyboardFocusable: isFocusZoneEnabled,
            numChecklistItems: task.numberOfChecklistItems,
            numCompletedChecklistItems: task.numberOfChecklistItems - task.numberOfActiveChecklistItems,
            onKeyDown: disableFocusZoneCallback
        }) : null;
        const myDayIndicator = task.isOnMyDay ? /*#__PURE__*/ React.createElement(MyDayIndicator, {
            ariaLabel: Strings.TaskGrid_MyDayIndicatorIconTooltip,
            isKeyboardFocusable: isFocusZoneEnabled,
            onKeyDown: disableFocusZoneCallback
        }) : null;
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.indicatorRow
        }, /*#__PURE__*/ React.createElement("div", {
            className: "leftSection"
        }, renderPriorityIndicator(), renderInProgressIndicator(), conversationIndicator, attachmentIndicator, checkListIndicator, myDayIndicator, appPoweredTaskIndicator));
    };
    /** Render the bucket name element & quick command set bucket menu */ const renderBucketTitle = ()=>{
        if (taskBucket == null || !showBucketTitle) {
            return null;
        }
        const bucketNameClass = taskPermissions.canUpdateBucket ? "bucketName" : "bucketName disableUpdateBucket";
        if (callbacks.getBuckets == null) {
            return /*#__PURE__*/ React.createElement("div", {
                className: bucketNameClass
            }, taskBucket.title);
        }
        const buckets = callbacks.getBuckets(task.planId) ?? [];
        const menuItems = buckets// Buckets sorting is descending order
        .sort((bucket1, bucket2)=>compareOrderHints(bucket2.orderHint, bucket1.orderHint)).map((bucket)=>({
                key: bucket.id,
                name: bucket.title,
                onClick: onClickBucketMenuItem
            }));
        const contextMenu = /*#__PURE__*/ React.createElement(ContextualMenu, {
            shouldFocusOnMount: true,
            items: menuItems,
            className: "bucketContextualMenu",
            directionalHint: DirectionalHint.bottomAutoEdge,
            target: bucketMenuButtonRef,
            onDismiss: hideBucketMenu
        });
        /* eslint-disable jsx-a11y/anchor-is-valid */ return /*#__PURE__*/ React.createElement("div", {
            className: bucketNameClass,
            ref: bucketMenuButtonRef,
            tabIndex: -1
        }, /*#__PURE__*/ React.createElement("a", {
            onClick: !taskPermissions.canEdit || !taskPermissions.canUpdateBucket ? undefined : toggleBucketMenu,
            "data-is-focusable": "false"
        }, taskBucket.title), isBucketMenuVisible && contextMenu);
    };
    /** Render a checklist preview. This omits all checked items, and only returns the top 10. */ const renderChecklistPreview = ()=>{
        if (!taskDetails) {
            return null;
        }
        checklistItemFocusableElements.current = [];
        const checklistItemsForPreview = ChecklistItem.getSortedChecklistItems(taskDetails.checklist).filter((value)=>!value.isChecked).slice(0, 10).map((value)=>{
            return /*#__PURE__*/ React.createElement("div", {
                className: "checklistItem",
                key: value.id
            }, /*#__PURE__*/ React.createElement("div", {
                className: "clickWrapper",
                title: isChecklistDisabled ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : value.title,
                onClick: stopPropagation,
                onKeyUp: stopPropagation
            }, /*#__PURE__*/ React.createElement(Checkbox, {
                inputProps: {
                    "data-is-focusable": true
                },
                label: value.title,
                className: "checklistItemText",
                onChange: (ev)=>onPreviewCheckboxChanged(value, ev),
                componentRef: (checklistItem)=>{
                    if (checklistItem != null) {
                        checklistItemFocusableElements.current.push({
                            element: checklistItem,
                            id: value.id
                        });
                    }
                },
                disabled: isChecklistDisabled,
                appTheme: theme
            })));
        });
        return /*#__PURE__*/ React.createElement("div", {
            className: "checklistPreview"
        }, checklistItemsForPreview);
    };
    /**
     * Render the notes preview
     * For Rich text Editor we need to extract content from html to render.
     * For plain text , we need to convert URL to renderable links.
     */ const renderNotesPreview = ()=>{
        const notesContent = taskDetails?.notes?.content;
        if (!notesContent) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: "notesPreview"
        }, extractContentFromHtmlForNotesPreview(notesContent, taskDetails.id));
    };
    /** Render the description text for the task's reference preview const */ const renderTaskReferencePreviewDescription = ()=>{
        const numberOfVisibleReferences = task.getNumberOfVisibleReferences();
        if (!taskDetails) {
            if (numberOfVisibleReferences > 0) {
                return /*#__PURE__*/ React.createElement("div", {
                    className: "referencePreviewDescription",
                    "data-is-focusable": "false"
                }, /*#__PURE__*/ React.createElement("span", {
                    className: "previewCaption"
                }));
            }
            return null;
        }
        const referenceWithLowestPriority = taskDetails.getReferenceWithLowestPriority();
        if (!isPreviewTypeReferenceOrAutomatic() || !referenceWithLowestPriority || !referenceWithLowestPriority.taskAttachmentType || referenceWithLowestPriority.taskAttachmentType.taskAttachmentType === SupportedReferenceTypes.TeamsHostedApp) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(DocumentPreview, {
            isFocusable: isFocusZoneEnabled,
            attachment: referenceWithLowestPriority,
            onKeyDown: disableFocusZone,
            onAttachmentClick: onAttachmentClick
        });
    };
    /** Method to render the preview section of a task card */ const renderPreview = ()=>{
        if (!showPreview) {
            return null;
        }
        // If we have a task details for this task, use it to find a preview to display.
        let previewElement = null;
        switch(task.previewType){
            case PreviewType.CheckList:
                previewElement = renderChecklistPreview();
                break;
            case PreviewType.Description:
                previewElement = renderNotesPreview();
                break;
            case PreviewType.Reference:
            case PreviewType.Automatic:
                previewElement = renderTaskReferencePreviewDescription();
                break;
            case PreviewType.NoPreview:
                break;
            default:
                ErrorUtilities.unreachableCase(task.previewType, (log)=>{
                    traceLogger.logTrace(0x1e20d485 /* tag_4insf */ , TraceLevel.Error, log);
                });
                break;
        }
        if (!previewElement) {
            return null;
        }
        // If we found a preview to display, display it.
        return previewElement && /*#__PURE__*/ React.createElement("div", {
            className: classNames.preview
        }, previewElement);
    };
    /** Render the top section of the task card */ const renderTopBar = ()=>{
        const taskTitleAriaLabel = StringHelper.format(Strings.TaskCard_TaskTitleAriaLabel, task.title);
        const title = /*#__PURE__*/ React.createElement("div", {
            className: "titleRow"
        }, renderCompleteButton(), /*#__PURE__*/ React.createElement("div", {
            className: "title",
            "aria-label": taskTitleAriaLabel,
            role: "textbox"
        }, task.title));
        let planName = null;
        if (showPlanTitle) {
            const planTitleAriaLabel = taskPlan != null ? StringHelper.format(Strings.TaskCard_PlanTitleAriaLabel, taskPlan.title) : undefined;
            planName = /*#__PURE__*/ React.createElement("div", {
                className: "planName",
                "aria-label": planTitleAriaLabel,
                role: taskPlan != null ? "textbox" : undefined
            }, taskPlan?.title);
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.topBar,
            onClick: onCardClick
        }, planName, title, renderBucketTitle(), renderPreview(), renderIndicatorRow());
    };
    /** Render the bottom bar section of the task card for Completed tasks */ const renderCompletedTaskBottomBar = ()=>{
        if (task.completedBy == null || task.completedDate == null) {
            return null;
        }
        const formattedCompletedDate = FormatDate(task.completedDate);
        let user = callbacks.getUser(task.completedBy);
        if (user == null) {
            // The user id was not found in the store
            user = UnknownUser.getUnknownUser(task.completedBy);
        }
        const completedByText = StringHelper.format(Strings.TaskCard_CompletedByOnDate, user.formattedDisplayName, formattedCompletedDate);
        const userPersonaProps = {
            text: completedByText,
            imageUrl: callbacks.getUserPhoto(user.id) || undefined,
            imageInitials: getInitials(user.displayName, getRTL()),
            initialsColor: getPersonaInitialsColor({
                text: user.displayName
            }),
            size: PersonaSize.size32,
            className: "completedBy"
        };
        return /*#__PURE__*/ React.createElement("div", {
            className: "completedAssignment"
        }, /*#__PURE__*/ React.createElement(UserContainer, {
            user: user,
            onLoad: callbacks.onRenderPersona,
            containerId: `userContainer-taskCard-${task.id}-${user.id}`
        }, /*#__PURE__*/ React.createElement(Persona, userPersonaProps)));
    };
    /** Render the due date indicator and the quick calendar picker */ const renderDueDateIndicator = ()=>{
        let ariaLabel;
        if (task.dueDate) {
            if (task.recurrence?.isActive()) {
                // "LL" format provides locale support
                ariaLabel = StringHelper.format(Strings.TaskCard_RecurringDueDateAriaLabel, task.dueDate.format("LL"));
            } else {
                // "LL" format provides locale support
                ariaLabel = StringHelper.format(Strings.TaskCard_DueDateAriaLabel, task.dueDate.format("LL"));
            }
        } else {
            ariaLabel = Strings.AddTaskCard_DueDateText;
        }
        if (!taskPermissions.canUpdateDueDate) {
            ariaLabel = Strings.SharedComponents_ReadonlyFieldPermissionsTooltip;
        }
        // If the user cannot edit the task or update the due date, and the task does not have a due date, do not render the due date indicator
        if ((!taskPermissions.canEdit || !taskPermissions.canUpdateDueDate) && !task.dueDate) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(DueDateIndicatorConvergence, {
            ariaLabel: ariaLabel,
            dueDate: task.dueDate?.toDate(),
            formattedDueDate: task.getFormattedDueDate(),
            isCurrentInstanceActive: task.recurrence?.isActive(),
            isKeyboardFocusable: isFocusZoneEnabled,
            isLate: task.isLate(),
            taskPermissions: taskPermissions,
            onKeyDown: disableFocusZoneCallback,
            onSetDueDate: (date)=>{
                const updatedTask = task.setDueDate(date == null ? null : moment(date));
                callbacks.onUpdateTask(task, updatedTask);
            }
        });
    };
    /** Render the assignment section of the task card */ const renderAssignmentBar = ()=>{
        const assignees = getAssignees();
        const canModifyAssigneePool = callbacks.getCanUserModifyAssigneePool(task.id) && taskPermissions.canEdit && taskPermissions.canAssign;
        return /*#__PURE__*/ React.createElement(TaskAssignmentControl, {
            layout: LayoutStyle.Compact,
            assignees: assignees,
            assignedAgent: callbacks.getCurrentlyAssignedAgent(task.id),
            availableAgents: availableAgents,
            currentUserId: currentUserId,
            canEditAssignments: taskPermissions.canAssign,
            canEditAccessPermissions: canModifyAssigneePool,
            canGiveAccessToGuests: canAddGuests,
            canAssignToResourcesWithoutAccess: canModifyAssigneePool,
            canAccessPlanViaSharedContainerOnly: callbacks.getCanUserAccessPlanViaSharedContainerOnly(task.planId),
            minQueryTermLength: MinCharsNeededToSearch,
            maxAssignees: maxAssignees,
            maxDisplayablePersonas: maxDisplayablePersonas,
            addIconPlacement: AddIconPlacement.BeforeAssignees,
            strings: assignmentControlStrings,
            onRenderPersona: callbacks.onRenderPersona,
            getUsersWithPlanAccess: getAssigneePoolCallback,
            getZeroTermQueryResults: getZeroTermQueryAssignmentResults,
            onAssignUser: onAssignUser,
            onUnassignUser: onUnassignUser,
            onAssignAgent: onAssignAgent,
            onUnassignAgent: onUnassignAgent,
            onGivePlanAccess: giveUserAccessToPlan,
            onOpenAssignCallout: onOpenAssignCallout,
            onCloseAssignCallout: hidePeoplePicker,
            onUserSearchQueryChanged: canModifyAssigneePool ? callbacks.onUserSearchQueryChanged : undefined,
            getUsersByKeyword: canModifyAssigneePool ? callbacks.getUsersByKeyword : undefined,
            isUsersByKeywordDataReady: canModifyAssigneePool ? callbacks.isUsersByKeywordDataReady : undefined,
            getUserPhoto: callbacks.getUserPhoto,
            showAssigneeLimitError: showAssigneeLimitError,
            alertUser: tenantSearchAlertUser,
            overflowButtonType: OverflowButtonType.descriptive,
            showPeoplePicker: isPeoplePickerVisible,
            targetElement: taskProgressState !== ProgressState.Complete && task.getAssigneeIds() !== null && task.getAssigneeIds().length > 0 ? bottomBarRef.current || undefined : containerRef.current || undefined,
            styles: TaskCardTaskAssignmentControlStyles(taskPermissions.canAssign, theme),
            allowMultiline: false,
            hideSingleAssigneeDetails: true,
            shouldHideFacepileBar: false,
            agentsInfoContentMap: agentsInfoContentMap
        });
    };
    /** Render the bottom bar section of the task card for Active tasks */ const renderActiveTaskBottomBar = ()=>{
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
            className: "bottomBarLeftSection"
        }, renderDueDateIndicator()), /*#__PURE__*/ React.createElement("div", {
            className: "bottomBarRightSection",
            onClick: (ev)=>{
                ev.preventDefault();
            }
        }, renderAssignmentBar()));
    };
    /**
     * Returns whether an agent is working on the task
     */ function isAgentWorkingOnTask() {
        const copilotWorkflowStateLabel = callbacks.getCopilotWorkflowStatusLabel(task.id);
        return copilotWorkflowStateLabel?.name === Strings.Label_CopilotWorkflow_InProgress;
    }
    /**
     *  Render the animated progress indicator if an agent is working on the task
     */ function renderAnimatedProgressIndicator() {
        const customStyles = customAgentStatusIndicatorStyles();
        return isAgentWorkingOnTask() ? /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(AgentStatusIndicator, {
            customStyles: customStyles
        }), /*#__PURE__*/ React.createElement(AnimatedProgressIndicator, {
            barHeight: 4,
            className: "animatedProgressIndicator"
        })) : null;
    }
    /** Render the bottom bar section of the task card */ const renderBottomBar = ()=>{
        const dueDateDependency = task.dueDate != null;
        const bottomBarClassName = css(classNames.bottomBar, taskProgressState === ProgressState.Complete || dueDateDependency || task.getAssigneeIds() !== null && task.getAssigneeIds().length > 0 || callbacks.getCurrentlyAssignedAgent(task.id) !== null ? "" : "noDisplayBottomBar");
        return /*#__PURE__*/ React.createElement("div", {
            className: bottomBarClassName,
            ref: bottomBarRef,
            tabIndex: -1
        }, taskProgressState === ProgressState.Complete && task.completedBy && task.completedDate ? renderCompletedTaskBottomBar() : renderActiveTaskBottomBar());
    };
    const taskCardContent = /*#__PURE__*/ React.createElement("div", {
        className: classNames.textContent,
        tabIndex: -1
    }, renderReferencePreviewThumbnail(), renderTaskMenu(), renderLabels(), renderTopBar(), renderAnimatedProgressIndicator(), renderBottomBar());
    const taskCardClassName = css(classNames.root, "passive_0", "active_2", {
        complete: taskProgressState === ProgressState.Complete
    }, {
        assigneeHighlighted: highlightType.assigneeHighlighted
    });
    const taskCardAriaLabel = task.title;
    /**
     * Note:
     * TaskCard is wrapped into a FocusZone to support arrow key navigation inside a task card.
     * By default, FocusZone is disabled. It's activated only when user uses right arrow key on TaskBoardCard.
     * Users will be able to use arrow keys (left & right) to access the actionable task card items like complete button, progress icon, due date, more command button and assignment card.
     * 'Tab' will take them out of the FocusZone.
     */ return /*#__PURE__*/ React.createElement(FocusZone, {
        direction: FocusZoneDirection.horizontal,
        isCircularNavigation: true,
        disabled: !isFocusZoneEnabled
    }, /*#__PURE__*/ React.createElement("div", {
        className: taskCardClassName,
        "aria-label": taskCardAriaLabel
    }, /*#__PURE__*/ React.createElement("div", {
        className: "container",
        ref: containerRef
    }, taskCardContent)));
//#endregion
}
/**
 * Task Card component that renders a card for a task
 */ export const TaskCard = styled(TaskCardBase, getStyles);
